import React, { useState } from 'react';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import styled from '@mui/system/styled';
import theme from '@/mui/theme';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import Typography from '@mui/material/Typography';
import { WPLink } from '@/data/wp/operations/queries/fragments/link';
import { getIconComponentByName } from '@/utils/icons-map';
import CmsLinks from '../ui/CmsLinks';
import Chip from '@mui/material/Chip';
import useMediaQuery from '@mui/material/useMediaQuery';

interface ProductOverlayAccordionProps {
  title?: string;
  accordions: {
    accordionTitle: string;
    accordionRow: {
      accordionIcon: string;
      accordionLabel: string;
      downloadLink: string;
      /** se true downloadLink e' gia' un url completo e non va prefissato con /download/ (es: galleria immagini) */
      directLink?: boolean;
    }[];
    accordionBtn: WPLink[];
  }[];
  btnDownloadAll: WPLink[];
  btnDownloadAllMobile: WPLink[];
  downloadGaranzia?: {
    garanziaLink?: string;
    garanziaIcon?: string;
    garanziaLabel?: string;
  };
  useTag?: {
    title?: string;
    tagList?: string[];
  }
};

interface TabPanelProps extends React.HTMLAttributes<HTMLDivElement> {
  children?: React.ReactNode
  index: number
  value: number
}

const TabPanel: React.FC<TabPanelProps> = (props) => {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`product-detail-tab-${index}`}
      aria-labelledby={`product-detail-tabpanel-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ pt: '13px' }}>
          <Box>{children}</Box>
        </Box>
      )}
    </div>
  );
};

const ExternalStyledTitle = styled(Typography)({
  [theme.breakpoints.up('sm')]: {
    fontSize: '24px',
    letterSpacing: '-0.1px',
    lineHeight: '30px',
  },
  color: '#182641',
  fontSize: '16px',
  fontWeight: '800',
  letterSpacing: '0.1px',
  lineHeight: '24px',
  margin: '38px auto 20px 23px',
});

const StyledTitle = styled(Typography)({
  color: '#000000',
  fontSize: '16px',
  fontWeight: '800',
  letterSpacing: '0.1px',
  lineHeight: '24px',
  paddingLeft: '6px'
});

const StyledAccordion = styled(Accordion)({
  backgroundColor: '#f1f1f1',
  border: '0',
  boxShadow: 'unset',
  marginBottom: '3px',
  '.MuiAccordionDetails-root': {
    padding: '0',
    backgroundColor: '#ffffff',
  },
  '&::before': {
    display: 'none',
  }
});

const StyledAccordionSummary = styled(AccordionSummary)({
  padding: '0 20px',
  "&.Mui-expanded .Mui-expanded": {
    transform: "rotate(0deg)",
  },
});

const StyledTabPanel = styled(TabPanel)({
  '&.tab-0': {
    '.MuiTableHead-root': {
      'th': {
        '&:last-of-type, &:nth-last-of-type(2)': {
          textAlign: 'center',
        },
      }
    }
  },
  '&.tab-1': {
    [theme.breakpoints.up('sm')]: {
      '.MuiTableHead-root': {
        'th': {
          minWidth: '200px'
        }
      },
    },
    '.MuiTableBody-root': {
      'th:first-of-type': {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }
    }
  }
});

const StyledBox = styled(Box)({
  [theme.breakpoints.up('sm')]: {
    marginBottom: '15px',
  },
  display: 'flex',
  alignItems: 'center',
  lineHeight: 'normal',
  paddingLeft: '26px',
  marginBottom: '25px',
  fontSize: '14px',
  'a': {
    color: '#000000',
    textDecoration: 'none'
  },
  'svg': {
    width: '25.5px',
    height: '33px',
    marginRight: '13px'
  }
});

const StyledCmsLinks = styled(CmsLinks)({
  position: 'relative',
  margin: '50px 0 0',
  padding: '26px 0 0',
  alignItems: 'center',
  justifyContent: 'center',
  '&::after': {
    content: '""',
    position: 'absolute',
    left: '-32px',
    top: '0',
    width: 'calc(100% + 64px)',
    height: '1px',
    backgroundColor: '#979797',
  },
  'a.MuiButton-root': {
    padding: '16px 54px',
  },
  [theme.breakpoints.up('sm')]: {
    margin: '38px 0 0',
    padding: '0',
    alignItems: 'unset',
    '&::after': {
      display: 'none',
    },
    'svg': {
      color: '#ffffff',
      width: '16px',
      height: '17px',
    },
    'a.MuiButton-root': {
      padding: '16px 22px',
    }
  },
});

const StyledGaranzia = styled(Box)({
  margin: '52px 26px 56px',
  'a': {
    display: 'flex',
    alignItems: 'center',
    textDecoration: 'none',
    color: '#182641',
    fontSize: '22px',
    fontWeight: '600',
    letterSpacing: '-0.45px',
    lineHeight: '28px',
    'svg': {
      marginRight: '15px',
    }
  }
});

const StyledChip = styled(Chip)({
  borderRadius: '5px',
  border: '1px solid #606060',
  padding: '0 3.5px',
  margin: '0 18px 12px 0',
  color: '#606060',
  fontSize: '12px',
  fontWeight: 'bold',
  letterSpacing: '0',
  lineHeight: '20px',
  textAlign: 'center',
  height: '27px'
});


const ProductOverlayAccordion: React.FC<ProductOverlayAccordionProps> = ({ title, accordions, btnDownloadAll, btnDownloadAllMobile, downloadGaranzia, useTag }) => {
  const matches = useMediaQuery('(min-width:600px)');
  const [expandedIndex, setExpandedIndex] = useState<number | false>(false);
  const [expandedChildIndex, setExpandedChildIndex] = useState<number | false>(false);

  return <Grid item xs={12} sx={{ marginLeft: { sm: '30px', xs: 0 }, marginTop: { sm: 0, xs: '50px' }, borderLeft: { sm: '2px solid #f5f5f5', xs: 0 }, paddingLeft: { sm: '65px', xs: 0 }, height: { sm: '100%', xs: 'auto' }}}>
    {title && matches &&
      <ExternalStyledTitle>{title}</ExternalStyledTitle>
    }
    {accordions.length > 0 && accordions.map((singleAccordion, index) => {
       const isExpanded = expandedIndex === index;
      return <StyledAccordion
        square={true}
        disableGutters={true}
        key={index}
        expanded={isExpanded}
            onChange={() => setExpandedIndex(isExpanded ? false : index)}
      >
        <StyledAccordionSummary expandIcon={isExpanded? getIconComponentByName('minus') : getIconComponentByName('plus')} key={index}>
          <StyledTitle>{singleAccordion.accordionTitle}</StyledTitle>
        </StyledAccordionSummary>
        <AccordionDetails>
          <StyledTabPanel className={`tab-${index}`} value={index} index={index}>
            {/* {singleAccordion.accordionRow && singleAccordion.accordionRow.map((singleRow, index) => {
              return <StyledBox key={index}>
                <a href={"/download/" + singleRow.downloadLink + "/" + singleRow.accordionLabel} download>
                  {getIconComponentByName(singleRow.accordionIcon)}
                  {singleRow.accordionLabel}
                </a>
              </StyledBox>
            })} */}
            {singleAccordion.accordionRow && singleAccordion.accordionRow.map((singleRow, index) => {
              // Se il row contiene dei sotto-elementi (accordionChildren), creiamo un sub-accordion
              // Altrimenti lo mostriamo come semplice link di download
              const hasChildren = Array.isArray((singleRow as any).accordionChildren) && (singleRow as any).accordionChildren.length > 0;

              return (
                <React.Fragment key={index}>
                  {!hasChildren && (
                    <StyledBox>
                      <a
                        href={singleRow.directLink ? singleRow.downloadLink : "/download/" + singleRow.downloadLink + "/" + singleRow.accordionLabel}
                        download={!singleRow.directLink}
                      >
                        {getIconComponentByName(singleRow.accordionIcon)}
                        {singleRow.accordionLabel}
                      </a>
                    </StyledBox>
                  )}

                  {hasChildren && (
                    <StyledAccordion
                      square={true}
                      disableGutters={true}
                      expanded={expandedChildIndex === index}
                      onChange={() =>
                        setExpandedChildIndex(expandedChildIndex === index ? false : index)
                      }
                      sx={{ backgroundColor: '#fff', boxShadow: 'none', borderTop: '1px solid #e0e0e0' }}
                    >
                      <StyledAccordionSummary expandIcon={expandedChildIndex === index ? getIconComponentByName('minus') : getIconComponentByName('plus')}>
                        <StyledTitle>{singleRow.accordionLabel}</StyledTitle>
                      </StyledAccordionSummary>
                      <AccordionDetails sx={{ backgroundColor: '#fff' }}>
                        {(singleRow as any).accordionChildren.map((child: any, cIndex: number) => (
                          <StyledBox key={cIndex}>
                            <a href={"/download/" + child.downloadLink + "/" + child.accordionLabel} download>
                              {getIconComponentByName(child.accordionIcon)}
                              {child.accordionLabel}
                            </a>
                          </StyledBox>
                        ))}
                      </AccordionDetails>
                    </StyledAccordion>
                  )}
                </React.Fragment>
              );
            })}
            {singleAccordion.accordionBtn && singleAccordion.accordionBtn.length > 0 &&
              <CmsLinks data={singleAccordion.accordionBtn} sx={{ margin: { sm: '19px 30px 0', xs: '25px 30px 0' }, paddingBottom: '28px' }}></CmsLinks>
            }
          </StyledTabPanel>
        </AccordionDetails>
      </StyledAccordion>
    })}
    {btnDownloadAll && btnDownloadAll.length > 0 && matches &&  
      <StyledCmsLinks data={btnDownloadAll}></StyledCmsLinks>
    }
    {btnDownloadAllMobile && btnDownloadAllMobile.length > 0 && !matches &&
      <StyledCmsLinks data={btnDownloadAllMobile}></StyledCmsLinks>
    }
    {downloadGaranzia && downloadGaranzia.garanziaIcon && matches && false &&
      <StyledGaranzia sx={{ margin: '52px 26px 56px' }}>
        <a href={downloadGaranzia?.garanziaLink}>{getIconComponentByName(downloadGaranzia?.garanziaIcon)}{downloadGaranzia?.garanziaLabel}</a>
      </StyledGaranzia>
    }
    {useTag && useTag.title && matches && <>
      <ExternalStyledTitle>{useTag.title}</ExternalStyledTitle>
      <Box sx={{ margin: '0 23px 0' }}>
        {useTag.tagList && useTag.tagList.map((singleTag, index) => <StyledChip key={index} label={singleTag} variant="outlined"></StyledChip>)}
      </Box>
      </>}
  </Grid>
}

export default ProductOverlayAccordion;
